import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { useMutation } from 'Data/Api.js'
import { useDataValue } from 'Simple/Data'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { toLocalTimestampLiteral } from 'Data/format.js'
import { FREQUENTLY_USED_VALUE_KEYS } from 'Data/constants.js'
import { setFrequentlyUsedSelectionValue } from 'Logic/localStorage.js'
import mutationGenerateLetter from './mutation-generate-letter.graphql.js'
import mutationUpdateResource from './mutation-update-resource.graphql.js'
import mutationUpdateTaskAssignee from './mutation-update-task-assignee.graphql.js'
import mutationDeleteTaskAssignee from './mutation-delete-task-assignee.graphql.js'
import mutationUpdateTaskPriority from './mutation-update-task-priority.graphql.js'
import mutationUpdateTaskTitle from './mutation-update-task-title.graphql.js'
import mutationUpdateTaskDescription from './mutation-update-task-description.graphql.js'
import mutationUpdateTaskPatient from './mutation-update-task-patient.graphql.js'
import mutationUpdateTaskTags from './mutation-update-task-tags.graphql.js'
import mutationUpdateTaskDueDate from './mutation-update-task-due-date.graphql.js'
import mutationDeleteTaskTag from './mutation-delete-task-tag.graphql.js'
import mutationCreateTaskComment from './mutation-create-task-comment.graphql.js'
import mutationUpdateResourceResourceId from './mutation-update-resource-resource_id.graphql.js'
import mutationUpdateResourceMetadataRecipientId from './mutation-update-resource-metadata-recipient_id.graphql.js'
import mutationUpdateResourceMetadataPostalAddressId from './mutation-update-resource-metadata-postal_address_id.graphql.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)

  let [, executeMutationUpdateResource] = useMutation(mutationUpdateResource)
  let [, executeMutationGenerateLetter] = useMutation(mutationGenerateLetter)

  let [, executeMutationUpdateTaskAssignee] = useMutation(
    mutationUpdateTaskAssignee
  )
  let [, executeMutationDeleteTaskAssignee] = useMutation(
    mutationDeleteTaskAssignee
  )
  let [, executeMutationUpdateTaskTitle] = useMutation(mutationUpdateTaskTitle)
  let [, executeMutationUpdateTaskDescription] = useMutation(
    mutationUpdateTaskDescription
  )
  let [, executeMutationUpdateTaskPriority] = useMutation(
    mutationUpdateTaskPriority
  )
  let [, executeMutationUpdateTaskPatient] = useMutation(
    mutationUpdateTaskPatient
  )
  let [, executeMutationDeleteTaskTag] = useMutation(mutationDeleteTaskTag)
  let [, executeMutationUpdateTaskTags] = useMutation(mutationUpdateTaskTags)
  let [, executeMutationUpdateTaskDueDate] = useMutation(
    mutationUpdateTaskDueDate
  )
  let [, executeMutationCreateTaskComment] = useMutation(
    mutationCreateTaskComment
  )
  let [, executeMutationUpdateResourceResourceId] = useMutation(
    mutationUpdateResourceResourceId
  )
  let [, executeMutationUpdateResourceMetadataRecipientId] = useMutation(
    mutationUpdateResourceMetadataRecipientId
  )
  let [, executeMutationUpdateResourceMetadataPostalAddressId] = useMutation(
    mutationUpdateResourceMetadataPostalAddressId
  )

  let current_user_id = useDataValue({
    context: 'global',
    path: 'user.id',
    viewPath: props.viewPath,
  })

  let xid = useDataValue({
    context: 'global',
    path: 'current_location.xid',
    viewPath: props.viewPath,
  })

  let is_new_generate_letters_enabled = useDataValue({
    context: 'global',
    viewPath: props.viewPath,
    path: 'feature_flags.is_new_generate_letters_enabled',
  })

  return async function onSubmit({ args, value }) {
    switch (args.type) {
      case 'generate_letter': {
        let mutationResponse = await executeMutationGenerateLetter({
          patient_id: value.patient._id,
          document_template_id: value.resources[0].resource_id,
          recipient_id: value.resources[0].metadata?.recipient_id,
          postal_address_id: value.resources[0].metadata?.postal_address_id,
          appointment_id: value.resources[0].metadata?.appointment_id,
          booking_id: value.resources[0].metadata?.booking_id,
          tooth_chart_snapshot_id:
            value.resources[0].metadata?.tooth_chart_snapshot_id,
          tooth_marking_ids: value.resources[0].metadata?.tooth_marking_ids,
        })

        if (mutationResponse.error) {
          notify(notifyError('Error while generating letter.'))
          return
        }

        await executeMutationUpdateResource({
          resource_id: value.resources[0].id,
          document_id:
            mutationResponse.data.tasks_generate_letter.documentNodeId,
        })

        notify(notifySuccess(`Letter generated.`))
        setFlowTo(
          normalizePath(
            props.viewPath,
            is_new_generate_letters_enabled
              ? 'Content/Letter/Content'
              : 'Content/OldLetter/Content'
          )
        )
        break
      }

      case 'set_task_assignee': {
        let { error } = await executeMutationUpdateTaskAssignee({
          task_id: args.task_id,
          user_id: args.user_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the assignee. Please try again later.'
            )
          )
          return
        }

        if (args.person && args.person?.id !== current_user_id) {
          setFrequentlyUsedSelectionValue({
            key: FREQUENTLY_USED_VALUE_KEYS.TASK_ASSIGNEES,
            value: args.person,
            xid,
          })
        }

        break
      }

      case 'delete_task_assignee': {
        let { error } = await executeMutationDeleteTaskAssignee({
          task_id: args.task_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while deleting the assignee. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_title': {
        let { error } = await executeMutationUpdateTaskTitle({
          task_id: args.task_id,
          title: args.title,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the title. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_description': {
        let { error } = await executeMutationUpdateTaskDescription({
          task_id: args.task_id,
          description: args.description,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the description. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_priority': {
        let { error } = await executeMutationUpdateTaskPriority({
          task_id: args.task_id,
          priority: args.priority,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the priority. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_due_date': {
        let { error } = await executeMutationUpdateTaskDueDate({
          task_id: args.task_id,
          due_date: args.due_date && toLocalTimestampLiteral(args.due_date),
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the due date. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_patient': {
        let { error } = await executeMutationUpdateTaskPatient({
          task_id: args.task_id,
          patient_id: args.patient_id,
        })

        if (error) {
          notify(
            notifyError('Error while changing patient. Please try again later.')
          )
          return
        }

        break
      }

      case 'set_task_tags': {
        let { error } = await executeMutationUpdateTaskTags({
          task_id: args.task_id,
          task_tags: args.tag_ids.map(tag_id => ({
            task_id: args.task_id,
            tag_id,
          })),
        })

        if (error) {
          notify(
            notifyError('Error while updating tags. Please try again later.')
          )
          return
        }

        break
      }

      case 'delete_task_tag': {
        let { error } = await executeMutationDeleteTaskTag({
          id: args.id,
        })

        if (error) {
          notify(
            notifyError('Error while updating tags. Please try again later.')
          )
          return
        }

        break
      }

      case 'create_task_comment': {
        let { error } = await executeMutationCreateTaskComment({
          task_id: args.task_id,
          comment: args.comment,
        })

        if (error) {
          notify(
            notifyError(
              'Error while creating task comment. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_action_resource_id': {
        let { error } = await executeMutationUpdateResourceResourceId({
          id: args.action_id,
          resource_id: args.resource_id,
        })

        if (error) {
          notify(
            notifyError(
              'Error while changing the document template. Please try again later.'
            )
          )
          return
        }

        break
      }

      case 'set_task_action_metadata_recipient_id_and_postal_address_id': {
        let { error: recipientError } =
          await executeMutationUpdateResourceMetadataRecipientId({
            id: args.action_id,
            recipient_id: args.recipient_id,
          })

        if (recipientError) {
          notify(
            notifyError(
              'Error while changing the recipient. Please try again later.'
            )
          )
          return
        }

        if (args.postal_address_id) {
          let { error: postalError } =
            await executeMutationUpdateResourceMetadataPostalAddressId({
              id: args.action_id,
              postal_address_id: args.postal_address_id,
            })

          if (postalError) {
            notify(
              notifyError(
                'Error while changing the postal address. Please try again later.'
              )
            )
            return
          }
        }

        break
      }

      default:
    }
  }
}
